import type { Circuit, PlayerTrackPosition } from '../types/track.js';
import { hasStartFinishCellOverlap } from './circuitLoop.js';
import { normalizeMainPathIndex } from './jokerLap.js';
import type { StartGridMode } from './startLane.js';

function isOnMainTrackForLapCount(position: PlayerTrackPosition): boolean {
  return position.startLaneIndex == null && position.jokerBranchIndex == null;
}

/** Franchissements de ligne d’arrivée à ignorer (grille sur ligne de départ). */
export interface StartLineCrossingSkip {
  remaining: number;
}

function creditLapCrossings(laps: number, crossing: number, skip?: StartLineCrossingSkip): number {
  if (crossing <= 0) return laps;
  if (skip && skip.remaining > 0) {
    const ignored = Math.min(skip.remaining, crossing);
    skip.remaining -= ignored;
    return laps + (crossing - ignored);
  }
  return laps + crossing;
}

/**
 * Compte un passage de ligne d’arrivée entre deux positions consécutives
 * (wrap avant sur le path principal uniquement).
 */
export function countLapCrossingBetween(
  circuit: Circuit,
  from: PlayerTrackPosition,
  to: PlayerTrackPosition
): number {
  if (!isOnMainTrackForLapCount(from) || !isOnMainTrackForLapCount(to)) {
    return 0;
  }
  const pathLen = circuit.path.length;
  if (pathLen <= 0) return 0;
  const prevIdx = normalizeMainPathIndex(from.mainPathIndex, pathLen);
  const currIdx = normalizeMainPathIndex(to.mainPathIndex, pathLen);
  const sfOverlap = hasStartFinishCellOverlap(circuit);
  // Boucle S/F (même case) : le crédit est à l’arrivée sur pathLen-1.
  // Le wrap logique pathLen-1 → 0 ne doit pas recompter (sinon arrêt sur la ligne = +2).
  if (sfOverlap && prevIdx === pathLen - 1 && currIdx === 0) return 0;
  if (currIdx < prevIdx) return 1;
  if (sfOverlap && currIdx === pathLen - 1 && prevIdx < pathLen - 1) return 1;
  return 0;
}

/** Met à jour le nombre de tours complétés à partir des waypoints réels du déplacement. */
export function addLapsFromWaypointPath(
  currentLapCount: number,
  circuit: Circuit,
  waypoints: readonly PlayerTrackPosition[],
  skipStartLineCrossings?: StartLineCrossingSkip
): number {
  if (waypoints.length < 2) return currentLapCount;
  let laps = currentLapCount;
  const sfOverlap = hasStartFinishCellOverlap(circuit);
  let sfFinishCrossingCredited = false;
  for (let i = 1; i < waypoints.length; i++) {
    let crossing = countLapCrossingBetween(circuit, waypoints[i - 1], waypoints[i]);
    if (sfOverlap && crossing > 0) {
      if (sfFinishCrossingCredited) crossing = 0;
      else sfFinishCrossingCredited = true;
    }
    laps = creditLapCrossings(laps, crossing, skipStartLineCrossings);
  }
  return laps;
}

/**
 * Met à jour le nombre de tours complétés après un déplacement.
 * Préfère les waypoints ; repli sur le delta d’index si nécessaire.
 */
export function addLapsForMove(
  currentLapCount: number,
  circuit: Circuit,
  from: PlayerTrackPosition,
  to: PlayerTrackPosition,
  waypoints?: readonly PlayerTrackPosition[],
  skipStartLineCrossings?: StartLineCrossingSkip
): number {
  if (waypoints && waypoints.length >= 2) {
    return addLapsFromWaypointPath(currentLapCount, circuit, waypoints, skipStartLineCrossings);
  }
  const pathLength = circuit.path.length;
  if (pathLength <= 0) return currentLapCount;
  if (!isOnMainTrackForLapCount(from) || !isOnMainTrackForLapCount(to)) {
    return currentLapCount;
  }
  const fromIdx = normalizeMainPathIndex(from.mainPathIndex, pathLength);
  const toIdx = normalizeMainPathIndex(to.mainPathIndex, pathLength);
  const forwardDist = (toIdx - fromIdx + pathLength) % pathLength;
  if (forwardDist === 0) return currentLapCount;
  const crossing = countLapCrossingBetween(circuit, from, to);
  return creditLapCrossings(currentLapCount, crossing, skipStartLineCrossings);
}

/**
 * Franchissements S/F à ignorer au départ.
 * - Ligne de départ physique (startLane) : 1er passage ignoré.
 * - Demi / finale sans ligne de départ : idem (1er tour franchit la ligne sans compter).
 */
export function startLineCrossingsToSkipForGridPosition(
  position: PlayerTrackPosition,
  gridMode?: StartGridMode
): number {
  if (position.startLaneIndex != null) {
    // Jonction à l’index 0 : la ligne de départ rejoint le circuit juste après
    // la case S/F elle-même, donc le 1er retour à pathLen-1 est un tour complet
    // (et non un franchissement immédiat parasite) — ne pas l’ignorer.
    return position.mainPathIndex === 0 ? 0 : 1;
  }
  if (gridMode === 'final') return 1;
  return 0;
}
